import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';
import SettingsNav from './SettingsNav';

const PERMISSION_OPTIONS = [
  { key: 'manage_jobs', label: 'Manage job postings' },
  { key: 'manage_applications', label: 'Manage applications' },
  { key: 'schedule_interviews', label: 'Schedule interviews' },
  { key: 'manage_offers', label: 'Manage offers' },
  { key: 'view_analytics', label: 'View analytics' },
  { key: 'manage_billing', label: 'Manage billing' },
];

function EmployerTeam() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [savingPermissionsId, setSavingPermissionsId] = useState(null);

  // Draft permission edits, keyed by recruiter id. Kept separate from
  // `recruiters` so a refetch (triggered by invite/remove elsewhere on the
  // page) never silently clobbers an in-progress, unsaved edit.
  const [draftPermissions, setDraftPermissions] = useState({});

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  const fetchRecruiters = async () => {
    try {
      const res = await api.get('/recruiters');
      setRecruiters(res.data);
    } catch (err) {
      console.error('Failed to fetch recruiters', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchRecruiters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    setInviting(true);
    try {
      await api.post('/recruiters/invite', { email: inviteEmail, name: inviteName || undefined });
      setMessage('Invitation sent.');
      setInviteEmail('');
      setInviteName('');
      fetchRecruiters();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send invite.');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this team member? This cannot be undone.')) return;
    try {
      await api.delete(`/recruiters/${id}`);
      setDraftPermissions((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fetchRecruiters();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove team member.');
    }
  };

  // Effective permissions shown in the UI: draft if one exists, otherwise
  // whatever the server last returned.
  const permissionsFor = (recruiter) =>
    draftPermissions[recruiter.id] ?? recruiter.permissions ?? [];

  const isDirty = (recruiter) => draftPermissions[recruiter.id] !== undefined;

  const togglePermission = (recruiter, key) => {
    const current = permissionsFor(recruiter);
    const next = current.includes(key) ? current.filter((p) => p !== key) : [...current, key];
    setDraftPermissions((prev) => ({ ...prev, [recruiter.id]: next }));
  };

  const savePermissions = async (recruiter) => {
    const permissions = permissionsFor(recruiter);
    setSavingPermissionsId(recruiter.id);
    try {
      await api.put(`/recruiters/${recruiter.id}/permissions`, { permissions });
      // Success: fold the draft into confirmed server-shaped state and
      // clear the draft so the dirty indicator goes away.
      setRecruiters((prev) =>
        prev.map((r) => (r.id === recruiter.id ? { ...r, permissions } : r))
      );
      setDraftPermissions((prev) => {
        const next = { ...prev };
        delete next[recruiter.id];
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update permissions.');
      // Discard the failed draft and resync with the server so the UI
      // never shows a change that didn't actually persist.
      setDraftPermissions((prev) => {
        const next = { ...prev };
        delete next[recruiter.id];
        return next;
      });
      await fetchRecruiters();
    } finally {
      setSavingPermissionsId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} />

       <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} mobile />}

            <h2 className="text-xl sm:text-2xl font-bold mb-1 bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">Company Settings</h2>
            <p className="text-gray-500 text-sm mb-4">Manage your team and preferences</p>

            <SettingsNav />

            <div className="bg-white rounded-xl shadow p-5 sm:p-6 mb-6">
              <h3 className="font-bold text-[#0D1B3D] mb-3">Invite a team member</h3>
              {error && <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-3 text-sm">{error}</div>}
              {message && <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg mb-3 text-sm">{message}</div>}
              <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="recruiter@company.com"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                />
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Name (optional)"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                />
                <button
                  type="submit"
                  disabled={inviting}
                  className="bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white px-5 py-2.5 rounded-[18px] font-semibold text-sm shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
                >
                  {inviting ? 'Sending...' : '+ Send Invite'}
                </button>
              </form>
            </div>

            <div className="bg-white rounded-xl shadow p-5 sm:p-6">
              <h3 className="font-bold text-[#0D1B3D] mb-4">Team members</h3>

              {loading ? (
                <p className="text-gray-500 text-sm">Loading...</p>
              ) : recruiters.length === 0 ? (
                <p className="text-gray-500 text-sm">No team members yet — invite one above.</p>
              ) : (
                <div className="space-y-3">
                  {recruiters.map((r) => (
                                   <div key={r.id} className="border border-purple-100 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/15 hover:-translate-y-0.5">
                      <div className="flex items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                          <p className="font-semibold text-[#0D1B3D] truncate">{r.name}</p>
                          <p className="text-sm text-gray-500 truncate">{r.email}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span
                            className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
                              r.invite_status === 'accepted'
                                ? 'bg-[#ECFDF5] text-[#10B981]'
                                : 'bg-[#FFF7ED] text-[#F59E0B]'
                            }`}
                          >
                            {r.invite_status}
                          </span>
                          <button
                            type="button"
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                            className="text-[#7C3AED] text-xs font-semibold hover:underline"
                          >
                            {expandedId === r.id ? 'Hide permissions' : 'Permissions'}
                            {isDirty(r) && <span className="ml-1 text-[#F59E0B]">•</span>}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(r.id)}
                            className="text-red-500 text-xs font-semibold hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {expandedId === r.id && (
                        <div className="border-t border-gray-100 p-4 bg-gray-50/60">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            {PERMISSION_OPTIONS.map((opt) => (
                              <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={permissionsFor(r).includes(opt.key)}
                                  onChange={() => togglePermission(r, opt.key)}
                                  className="accent-[#7C3AED]"
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => savePermissions(r)}
                              disabled={savingPermissionsId === r.id || !isDirty(r)}
                              className="bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md shadow-purple-500/25 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 disabled:opacity-50"
                            >
                              {savingPermissionsId === r.id ? 'Saving...' : 'Save permissions'}
                            </button>
                            {isDirty(r) && !savingPermissionsId && (
                              <span className="text-xs text-[#F59E0B]">Unsaved changes</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployerTeam;